//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { courseAPI } from "./courseAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type CoursesDetails,
  type CreateCoursesInput,
  type UpdateCourseInput,
} from "./types/type";

/**
 * Custom hook `useCourses` to manage operations related to Courses, such as fetching, creating, updating, and deleting.
 * Also manages pagination and search functionality.
 *
 * @param {string} token - Authentication token for API requests.
 * @returns {Object} The hook returns Course data, loading state, error messages, and functions to interact with the API.
 */
export const useCourses = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "courses",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      courseAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const courses = data?.courses || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createCourseMutation = useMutation({
    mutationFn: (courseData: CreateCoursesInput) =>
      courseAPI.create(
        { ...courseData, description: courseData.description || "" },
        token
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
    },
  });

  const updateCourseMutation = useMutation({
    mutationFn: ({ id, ...courseData }: UpdateCourseInput) =>
      courseAPI.update(
        id,
        { ...courseData, description: courseData.description || "" },
        token
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
    },
  });

  const deleteCourseMutation = useMutation({
    mutationFn: (id: string) => courseAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
    },
  });

  const getCourseById = useCallback(
    async (
      id: string,
      options: {
        searchTermStudents?: string;
        studentFilter?: "all" | "active" | "inactive";
        pageStudents?: number;
        perPageStudents?: number;
        searchTermTeams?: string;
        pageTeams?: number;
        perPageTeams?: number;
      } = {}
    ): Promise<CoursesDetails | null> => {
      try {
        const unitDetails = (await courseAPI.getById(
          id,
          token,
          options
        )) as CoursesDetails;
        return unitDetails;
      } catch (err) {
        console.error("Error fetching unit by ID:", err);
        return null;
      }
    },
    [token]
  );

  return {
    courses,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createCourse: createCourseMutation.mutate,
    updateCourse: updateCourseMutation.mutate,
    deleteCourse: deleteCourseMutation.mutate,
    getCourseById,
    changePage,
    isCreating: createCourseMutation.isPending,
    isUpdating: updateCourseMutation.isPending,
    isDeleting: deleteCourseMutation.isPending,
  };
};
export { courseAPI };
